import type { UiIconName } from "@/components/UiIcon.vue";

/**
 * 사이드바 네비게이션 정의
 * - 프로토타입 셸(`_shell_before_pages.html`)의 사이드바 구조를 그대로 데이터화한 것이다. 라벨은 프로토타입 값 그대로다.
 * - 아이콘은 그룹(최상위 · 중첩)에만 둔다(80차 UXS80-01 · 리드 결정 L8). 이모지 32개는 OS 마다 모양이 다르고 잎 24개가
 *   그룹 아이콘과 겹쳐 뜻을 못 줘서 `UiIcon` 선 아이콘 7개로 바꿨다. 잎은 아이콘 없이 들여쓴다.
 * - `path` 는 `src/router.ts` 의 라우트 경로와 1:1 로 일치해야 한다.
 * - 개편(2026-10-13 ¶5 · ¶53~¶64): 「회사 대시보드」 를 「전체 현황 관리」 로 합쳤고, 조직적합성 잎만 ¶5 용어로 줄였으며
 *   (화면 제목 `meta.title` 은 그대로), 운영하기 맨 앞에 「채용 홈페이지 만들기」 를 두었다. 그룹 라벨 · 직무적합성 잎은 지시서
 *   표기 그대로다. 운영 · 관리 그룹에 중간 접힘 부모를 두지 않는다(¶61~¶64 는 그룹 바로 아래 나열).
 */

/** 최종 이동 대상 메뉴 항목 */
export interface NavItem {
  pageId: string;
  /** 잎은 아이콘을 두지 않는다(들여쓰기로 그룹에 속함을 보인다) */
  icon?: UiIconName;
  label: string;
  path: string;
}

/** 아코디언으로 열고 닫는 메뉴 그룹 (하위에 그룹 중첩 가능) */
export interface NavGroup {
  key: string;
  /** 그룹 선 아이콘(`UiIcon`) */
  icon?: UiIconName;
  label: string;
  children: (NavItem | NavGroup)[];
}

export function isNavGroup(node: NavItem | NavGroup): node is NavGroup {
  return "children" in node;
}

export const navigation: NavGroup[] = [
  {
    key: "start-flow",
    icon: "flag",
    label: "시작",
    children: [
      {
        pageId: "workspace",
        label: "워크스페이스 생성",
        path: "/start/workspace",
      },
      {
        pageId: "statusmatrix",
        label: "전체 현황 관리",
        path: "/start/status",
      },
    ],
  },
  {
    key: "quick-start",
    icon: "bolt",
    label: "새 채용시작",
    children: [
      {
        pageId: "applicantai",
        label: "빠르게 채용하기",
        path: "/quick-hire",
      },
    ],
  },
  {
    key: "custom-design",
    icon: "blueprint",
    label: "우리회사 맞춤 채용설계",
    children: [
      {
        key: "org-fit",
        icon: "building",
        label: "조직적합성 설계",
        children: [
          {
            pageId: "mission",
            label: "미션",
            path: "/org-fit/mission",
          },
          {
            pageId: "vision",
            label: "비전",
            path: "/org-fit/vision",
          },
          {
            pageId: "core",
            label: "핵심가치",
            path: "/org-fit/core-values",
          },
          {
            pageId: "talent",
            label: "인재상",
            path: "/org-fit/talent",
          },
          {
            pageId: "valuesystem",
            label: "가치관 체계도",
            path: "/org-fit/value-system",
          },
        ],
      },
      {
        key: "job-fit",
        icon: "puzzle",
        label: "직무적합성 설계",
        children: [
          {
            pageId: "jobanalysis",
            label: "직무분석",
            path: "/job-fit/job-analysis",
          },
          {
            pageId: "competency",
            label: "역량모델링",
            path: "/job-fit/competency",
          },
          {
            pageId: "ailiteracy",
            label: "AI 리터러시",
            path: "/job-fit/ai-literacy",
          },
          {
            pageId: "applicationdesign",
            label: "입사지원서 자동 설계하기",
            path: "/job-fit/application-design",
          },
          {
            pageId: "jobdictionary",
            label: "직무 사전",
            path: "/job-fit/dictionary",
          },
        ],
      },
    ],
  },
  {
    key: "operations",
    icon: "briefcase",
    label: "우리회사 채용전형 운영하기",
    children: [
      {
        pageId: "homepagebuilder",
        label: "채용 홈페이지 만들기",
        path: "/operations/careers-page",
      },
      {
        pageId: "applicants",
        label: "지원자 관리",
        path: "/operations/applicants",
      },
      {
        pageId: "interviewops",
        label: "면접준비",
        path: "/operations/interview-prep",
      },
      {
        pageId: "interview",
        label: "실시간 면접 콘솔",
        path: "/operations/interview-console",
      },
      {
        pageId: "referencecheck",
        label: "레퍼런스 체크",
        path: "/operations/reference-check",
      },
      {
        pageId: "decision",
        label: "최종 의사결정",
        path: "/operations/decision",
      },
      {
        pageId: "reports",
        label: "결과보고서",
        path: "/operations/reports",
      },
      {
        pageId: "onboardingdocs",
        label: "입사서류 요청하기",
        path: "/operations/onboarding-docs",
      },
    ],
  },
  {
    key: "management",
    icon: "settings",
    label: "셀렉허브 관리",
    children: [
      {
        pageId: "analytics",
        label: "셀렉허브 애널리틱스",
        path: "/management/analytics",
      },
      {
        pageId: "aigovernance",
        label: "AI 거버넌스",
        path: "/management/ai-governance",
      },
      {
        pageId: "admin",
        label: "SaaS 관리자",
        path: "/management/admin",
      },
      {
        pageId: "guardrails",
        label: "공정채용 체크",
        path: "/management/guardrails",
      },
    ],
  },
];

/** 트리를 평탄화한 메뉴 항목 목록 (모바일 셀렉트/라우트 매칭용) */
export const navItems: NavItem[] = (function flatten(
  nodes: (NavItem | NavGroup)[],
): NavItem[] {
  return nodes.flatMap((node) =>
    isNavGroup(node) ? flatten(node.children) : [node],
  );
})(navigation);

/**
 * 아코디언 키 → 포함된 pageId 목록
 * 프로토타입 `script_01.js` 의 `accordionPageMap` 과 동일한 역할을 한다.
 */
export const accordionPageMap: Record<string, string[]> = (function build(
  nodes: (NavItem | NavGroup)[],
  acc: Record<string, string[]>,
): Record<string, string[]> {
  for (const node of nodes) {
    if (!isNavGroup(node)) continue;
    build(node.children, acc);
    acc[node.key] = node.children.flatMap((child) =>
      isNavGroup(child) ? (acc[child.key] ?? []) : [child.pageId],
    );
  }
  return acc;
})(navigation, {});

/** pageId 로 메뉴 항목을 찾는다. */
export function findNavItemByPageId(pageId: string): NavItem | undefined {
  return navItems.find((item) => item.pageId === pageId);
}

/** 라우트 경로로 메뉴 항목을 찾는다. */
export function findNavItemByPath(path: string): NavItem | undefined {
  return navItems.find((item) => item.path === path);
}

/** 해당 pageId 를 품고 있어 자동으로 열려야 하는 아코디언 키 목록 */
export function getRelatedAccordionKeys(pageId: string): string[] {
  return Object.entries(accordionPageMap)
    .filter(([, pageIds]) => pageIds.includes(pageId))
    .map(([key]) => key);
}
